import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingCart, X, Plus, Minus, Trash2, ArrowLeft } from "lucide-react";
import { useCart } from "@/context/CartContext";

const WHATSAPP_NUMBER = "525522282990";

const buildWhatsAppMessage = ({ items, totalPrice, customer }) => {
  const lines = [];
  lines.push("🍕 *NUEVO PEDIDO - Adrianoz Pizza*");
  lines.push("");
  lines.push(`👤 *Cliente:* ${customer.name}`);
  lines.push(`📞 *Teléfono:* ${customer.phone}`);
  lines.push(`🏠 *Domicilio:* ${customer.address}`);
  lines.push(`📍 *Colonia:* ${customer.colonia}`);
  if (customer.generalNotes) lines.push(`📝 *Notas generales:* ${customer.generalNotes}`);
  lines.push("");
  lines.push("🛒 *Pedido:*");
  items.forEach((it, idx) => {
    lines.push(`${idx + 1}. ${it.name}`);
    if (it.orillaQueso) lines.push(`   + Orilla de queso (+$${it.orillaCost})`);
    if (it.notes) lines.push(`   Notas: ${it.notes}`);
    lines.push(`   Cantidad: ${it.quantity} - $${it.unitPrice * it.quantity}`);
    lines.push("");
  });
  lines.push(`💰 *TOTAL: $${totalPrice}*`);
  lines.push("");
  lines.push("🚚 _Entrega a domicilio_");
  return lines.join("\n");
};

const Field = ({ label, value, onChange, error, type = "text", testid, placeholder, rows }) => {
  const common = {
    value,
    onChange: (e) => onChange(e.target.value),
    placeholder,
    "data-testid": testid,
    className: `w-full bg-white border ${
      error ? "border-[#C41E3A]" : "border-[#1A1A1A]/15"
    } rounded-xl p-3 text-sm font-['DM_Sans'] focus:outline-none focus:border-[#556B2F]`,
  };
  return (
    <div>
      <label className="block font-['DM_Sans'] text-xs uppercase tracking-[0.18em] font-bold text-[#556B2F] mb-1.5">
        {label}
      </label>
      {rows ? <textarea rows={rows} {...common} /> : <input type={type} {...common} />}
      {error && <p className="mt-1 text-[11px] text-[#C41E3A]">{error}</p>}
    </div>
  );
};

const CheckoutForm = ({ customer, setCustomer, errors }) => {
  const update = (key) => (val) => setCustomer({ ...customer, [key]: val });
  return (
    <div className="p-5 space-y-4">
      <p className="font-['DM_Sans'] text-xs text-[#4A4A4A] bg-[#556B2F]/10 rounded-lg p-3">
        🚚 Solo entrega a domicilio. El envío es <b>gratis</b>. Te contactaremos por WhatsApp para
        confirmar tu pedido.
      </p>
      <Field label="Nombre completo" value={customer.name} onChange={update("name")} error={errors.name} placeholder="Juan Pérez" testid="customer-name" />
      <Field label="Teléfono" value={customer.phone} onChange={update("phone")} error={errors.phone} placeholder="55 1234 5678" type="tel" testid="customer-phone" />
      <Field label="Domicilio (calle y número)" value={customer.address} onChange={update("address")} error={errors.address} placeholder="Av. Chimalhuacán 100" testid="customer-address" />
      <Field label="Colonia" value={customer.colonia} onChange={update("colonia")} error={errors.colonia} placeholder="Col. Estado" testid="customer-colonia" />
      <Field label="Notas generales (opcional)" value={customer.generalNotes} onChange={update("generalNotes")} placeholder="Ej. timbre, referencias..." testid="customer-notes" rows={3} />
    </div>
  );
};

export const CartDrawer = () => {
  const { items, totalPrice, totalQty, drawerOpen, closeDrawer, updateQuantity, removeItem, clearCart } = useCart();
  const [view, setView] = useState("cart");
  const [customer, setCustomer] = useState({ name: "", phone: "", address: "", colonia: "", generalNotes: "" });
  const [errors, setErrors] = useState({});

  const handleClose = () => {
    closeDrawer();
    setTimeout(() => setView("cart"), 300);
  };

  const validate = () => {
    const e = {};
    if (!customer.name.trim()) e.name = "Requerido";
    if (!customer.phone.trim()) e.phone = "Requerido";
    else if (!/^\d[\d\s-]{7,}$/.test(customer.phone.trim())) e.phone = "Teléfono inválido";
    if (!customer.address.trim()) e.address = "Requerido";
    if (!customer.colonia.trim()) e.colonia = "Requerido";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    const msg = buildWhatsAppMessage({ items, totalPrice, customer });
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`;
    window.open(url, "_blank");
    clearCart();
    setCustomer({ name: "", phone: "", address: "", colonia: "", generalNotes: "" });
    setView("cart");
    closeDrawer();
  };

  return (
    <AnimatePresence>
      {drawerOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[350] bg-black/60 backdrop-blur-sm flex justify-end"
          onClick={handleClose}
        >
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 28, stiffness: 280 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full sm:max-w-md bg-[#FAF7F0] h-full flex flex-col shadow-2xl"
            data-testid="cart-drawer"
          >
            <div className="bg-[#2B3618] text-white p-5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                {view === "checkout" && (
                  <button onClick={() => setView("cart")} data-testid="back-to-cart-btn" className="w-9 h-9 bg-white/15 hover:bg-white/25 rounded-full flex items-center justify-center">
                    <ArrowLeft className="w-5 h-5" />
                  </button>
                )}
                <h2 className="font-['Playfair_Display'] text-xl font-bold">
                  {view === "cart" ? "Tu pedido" : "Datos para entrega"}
                </h2>
              </div>
              <button onClick={handleClose} data-testid="close-drawer-btn" className="w-9 h-9 bg-white/15 hover:bg-white/25 rounded-full flex items-center justify-center">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              {view === "cart" ? (
                items.length === 0 ? (
                  <div className="p-10 text-center text-[#4A4A4A]">
                    <ShoppingCart className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <p className="font-['DM_Sans']">Tu carrito está vacío.</p>
                    <p className="font-['DM_Sans'] text-xs mt-1">Agrega platillos del menú para empezar.</p>
                  </div>
                ) : (
                  <ul className="divide-y divide-[#1A1A1A]/10">
                    {items.map((it) => (
                      <li key={it.id} data-testid={`cart-item-${it.id}`} className="p-4 flex gap-3">
                        <div className="flex-1 min-w-0">
                          <span className="block text-[10px] uppercase tracking-[0.15em] font-bold text-[#556B2F] font-['DM_Sans']">{it.category}</span>
                          <h3 className="font-['DM_Sans'] text-sm font-bold text-[#1A1A1A] leading-snug">{it.name}</h3>
                          {it.orillaQueso && <p className="text-[11px] text-[#556B2F] mt-0.5">+ Orilla de queso (+${it.orillaCost})</p>}
                          {it.notes && <p className="text-[11px] text-[#4A4A4A] italic mt-0.5">"{it.notes}"</p>}
                          <div className="mt-2 flex items-center justify-between">
                            <div className="inline-flex items-center bg-white border border-[#1A1A1A]/15 rounded-full">
                              <button onClick={() => updateQuantity(it.id, -1)} data-testid={`decrease-${it.id}`} className="w-7 h-7 flex items-center justify-center hover:bg-[#F3F4F6] rounded-l-full">
                                <Minus className="w-3 h-3" />
                              </button>
                              <span className="w-7 text-center text-sm font-bold">{it.quantity}</span>
                              <button onClick={() => updateQuantity(it.id, 1)} data-testid={`increase-${it.id}`} className="w-7 h-7 flex items-center justify-center hover:bg-[#F3F4F6] rounded-r-full">
                                <Plus className="w-3 h-3" />
                              </button>
                            </div>
                            <span className="font-['DM_Sans'] text-base font-bold text-[#C41E3A]">${it.unitPrice * it.quantity}</span>
                          </div>
                        </div>
                        <button onClick={() => removeItem(it.id)} data-testid={`remove-${it.id}`} className="self-start w-8 h-8 rounded-full hover:bg-[#C41E3A]/10 text-[#C41E3A] flex items-center justify-center">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )
              ) : (
                <CheckoutForm customer={customer} setCustomer={setCustomer} errors={errors} />
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t border-[#1A1A1A]/10 bg-white p-4 space-y-3">
                <div className="flex items-baseline justify-between">
                  <span className="font-['DM_Sans'] text-sm text-[#4A4A4A]">
                    {totalQty} {totalQty === 1 ? "platillo" : "platillos"}
                  </span>
                  <span className="font-['Playfair_Display'] text-2xl font-bold text-[#2B3618]">${totalPrice}</span>
                </div>
                {view === "cart" ? (
                  <button onClick={() => setView("checkout")} data-testid="go-to-checkout-btn" className="w-full bg-[#C41E3A] hover:bg-[#9A172D] text-white font-['DM_Sans'] font-bold py-3 rounded-full transition-colors">
                    Continuar al pedido
                  </button>
                ) : (
                  <button onClick={handleSubmit} data-testid="submit-order-btn" className="w-full bg-[#25D366] hover:bg-[#20BD5C] text-white font-['DM_Sans'] font-bold py-3 rounded-full transition-colors flex items-center justify-center gap-2">
                    <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                    </svg>
                    Enviar pedido por WhatsApp
                  </button>
                )}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export const FloatingCartButton = () => {
  const { totalQty, openDrawer } = useCart();
  if (totalQty === 0) return null;
  return (
    <motion.button
      initial={{ scale: 0, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0, opacity: 0 }}
      onClick={openDrawer}
      data-testid="floating-cart-btn"
      className="fixed bottom-24 sm:bottom-28 right-5 sm:right-8 z-[200] w-14 h-14 bg-[#2B3618] hover:bg-[#3F4F22] text-white rounded-full shadow-xl shadow-black/30 flex items-center justify-center transition-colors"
      whileHover={{ scale: 1.06 }}
      whileTap={{ scale: 0.95 }}
    >
      <ShoppingCart className="w-6 h-6" />
      <span data-testid="cart-count-badge" className="absolute -top-1 -right-1 min-w-[22px] h-[22px] bg-[#C41E3A] text-white text-[11px] font-bold rounded-full flex items-center justify-center px-1.5 border-2 border-[#FAF7F0]">
        {totalQty}
      </span>
    </motion.button>
  );
};
